import { defineStore } from 'pinia';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import apiClient from '@/util/apiClient';
import posthog from '@/composables/usePostHog.js';
import { shouldThrottle } from '@/util/rateLimit.js';
import logger from '@/util/logging/logger.js';
import type { OfficialAutocompleteMetadata } from '@/util/officialAutocomplete';
import {
	BLOCK_TYPES,
	isAvailabilityBlockType,
	isBlockingBlockType,
	normalizeAvailabilityEntry,
	normalizeAvailabilityEntries,
	scopesMatchEntry,
	type AvailabilityBlockType,
	type AvailabilityEntry,
	type AvailabilitySuggestion,
	type DayCode,
	type ScopedAvailabilityInput,
	type ScopedAvailabilityStatus
} from '@/types/availability';

interface MatchDateEntry extends Record<string, unknown> {
	date?: string;
}
interface AvailabilityCheckResponse extends Record<string, unknown> {
	available?: boolean;
	hasAvailabilityEntry?: boolean;
	availabilityEntries?: AvailabilityEntry[];
	blockingEntries?: AvailabilityEntry[];
}
interface ErrorResponseBody {
	error?: string;
	message?: string;
}
interface StoredUserInfo {
	userNumber?: number;
}
interface QuickAddOptions {
	scope?: AvailabilityBlockType;
	groupNumber?: number | null;
	clientNumber?: number | null;
	startTime?: string | null;
	endTime?: string | null;
	allDay?: boolean;
	notes?: string | null;
}
interface FetchUserBlocksOptions {
	from?: string | Date | null;
	to?: string | Date | null;
}
interface SaveBlockInput {
	startDate: string | Date;
	endDate?: string | Date | null;
	startTime?: string | null;
	endTime?: string | null;
	allDay?: boolean;
	blockType?: AvailabilityEntry['blockType'];
	groupNumber?: number | null;
	clientNumber?: number | null;
	daysOfWeek?: string | DayCode[] | null;
	recurring?: boolean;
	notes?: string | null;
}

function createSaveBlockPayload(block: SaveBlockInput) {
	return {
		startDate: dayjs(block.startDate).format('YYYY-MM-DD'),
		endDate: dayjs(block.endDate || block.startDate).format('YYYY-MM-DD'),
		startTime: block.startTime || null,
		endTime: block.endTime || null,
		allDay: block.allDay !== false,
		blockType: block.blockType || BLOCK_TYPES.AVAILABLE_GLOBAL,
		groupNumber: block.groupNumber ? { groupNumber: block.groupNumber } : null,
		clientNumber: block.clientNumber ? { clientNumber: block.clientNumber } : null,
		daysOfWeek: block.daysOfWeek || null,
		recurring: block.recurring || false,
		notes: block.notes || null
	};
}

interface AvailabilityEntryWithBlockDate extends AvailabilityEntry {
	blockDate?: string | null;
}
interface AvailableOfficialsRequest {
	groupId: number;
	clientId?: number | null;
	locationId?: number | null;
	allowedRoles?: string[];
	filterMode?: string;
	matchNumber: number;
	matchDate?: string | null;
	matchTime?: string | null;
	includeAvailability?: boolean;
}
export interface AvailableOfficialDTO extends OfficialAutocompleteMetadata, Record<string, unknown> {
	userNumber: number;
	firstName?: string | null;
	middleName?: string | null;
	lastName?: string | null;
	birthdate?: string | null;
	photo?: string | null;
	label?: string | null;
	subLabel?: string | null;
	availabilityStatus?: string | null;
	availabilityReason?: string | null;
	disabled?: boolean | null;
	travelStatus?: string | null;
	travelMinutes?: number | null;
	venueToVenueMinutes?: number | null;
	estimatedArrival?: string | null;
	kickoffMarginMinutes?: number | null;
	preMatchMarginMinutes?: number | null;
	canOverride?: boolean | null;
	adjacentMatchNumber?: number | null;
	adjacentLocationName?: string | null;
}
interface ActionResult<T = undefined> {
	success: boolean;
	data?: T;
	error?: string;
	copied?: number;
	message?: string;
}
interface AvailabilityStoreState {
	blocks: AvailabilityEntry[];
	suggestions: AvailabilitySuggestion[];
	matchDates: MatchDateEntry[];
	loading: boolean;
	saving: boolean;
	error: string | null;
	selectedDates: string[];
	dragStart: string | null;
	dragEnd: string | null;
	isDragging: boolean;
	calendarView: 'month' | 'week' | 'agenda';
	mobileMode: boolean;
	lastFetchFrom: string | null;
	lastFetchTo: string | null;
	resizeHandler: (() => void) | null;
}
function coversDate(entry: Pick<AvailabilityEntry, 'startDate' | 'endDate'>, dateStr: string) {
	const start = dayjs(entry.startDate);
	const end = dayjs(entry.endDate);
	const date = dayjs(dateStr);
	return date.isSame(start, 'day') || date.isSame(end, 'day') || (date.isAfter(start) && date.isBefore(end));
}

function timeOnDate(date: dayjs.Dayjs, value: string | null | undefined) {
	if (!value) return null;
	const timeOnly = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
	if (!timeOnly) return null;
	let hour = Number(timeOnly[1]);
	const minute = Number(timeOnly[2]);
	const second = Number(timeOnly[3] ?? 0);
	const meridiem = timeOnly[4]?.toUpperCase();
	if (meridiem) {
		if (hour < 1 || hour > 12) return null;
		if (meridiem === 'PM' && hour !== 12) hour += 12;
		if (meridiem === 'AM' && hour === 12) hour = 0;
	}
	if (hour > 23 || minute > 59 || second > 59) return null;
	return date.hour(hour).minute(minute).second(second).millisecond(0);
}

function entryOverlapsMatchWindow(entry: AvailabilityEntry, date: dayjs.Dayjs, startTime?: string | null, endTime?: string | null) {
	const matchStart = timeOnDate(date, startTime);
	if (!matchStart) return true;
	let matchEnd = timeOnDate(date, endTime) ?? matchStart.add(2, 'hour');
	if (!matchEnd.isAfter(matchStart)) matchEnd = matchEnd.add(1, 'day');

	// Prefer concrete times when they are present. Older saved entries can retain
	// allDay: true even though their time range was later set in the calendar.
	if (!entry.startTime || !entry.endTime) return true;
	const blockStart = timeOnDate(date, entry.startTime);
	let blockEnd = timeOnDate(date, entry.endTime);
	if (!blockStart || !blockEnd) return true;
	if (!blockEnd.isAfter(blockStart)) blockEnd = blockEnd.add(1, 'day');
	return blockStart.isBefore(matchEnd) && matchStart.isBefore(blockEnd);
}

function blockingEntryAppliesToMatch(entry: AvailabilityEntry, groupNumber?: number | null, clientNumber?: number | null) {
	if (entry.blockType === BLOCK_TYPES.BLOCK_GLOBAL) return true;
	if (entry.blockType === BLOCK_TYPES.BLOCK_GROUP) return Number(entry.group?.groupNumber ?? entry['groupNumber']) === Number(groupNumber);
	if (entry.blockType === BLOCK_TYPES.BLOCK_CLIENT) return Number(entry.client?.clientNumber ?? entry['clientNumber']) === Number(clientNumber);
	return false;
}
function readStoredUserInfo(): StoredUserInfo {
	const raw = sessionStorage.getItem('user_info');
	if (!raw) {
		return {};
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') {
			return {};
		}
		const userNumberValue = (parsed as { userNumber?: unknown }).userNumber;
		if (typeof userNumberValue === 'number' && Number.isFinite(userNumberValue)) {
			return { userNumber: userNumberValue };
		}
		if (typeof userNumberValue === 'string') {
			const parsedUserNumber = Number(userNumberValue);
			if (Number.isFinite(parsedUserNumber)) {
				return { userNumber: parsedUserNumber };
			}
		}
		return {};
	} catch (error: unknown) {
		logger.warn('[AvailabilityStore] Failed to parse stored user info', {
			error: getErrorMessage(error)
		});
		return {};
	}
}

function getStoredUserNumber(): number | null {
	return readStoredUserInfo().userNumber ?? null;
}

function getErrorMessage(error: unknown, fallback = 'Request failed'): string {
	if (isAxiosError<ErrorResponseBody>(error)) {
		return error.response?.data.error || error.response?.data.message || error.message || fallback;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return fallback;
}

/**
 * Availability Store
 *
 * Manages user availability/blocks state and provides API integration
 * for the opt-in availability model.
 *
 * Key concepts:
 * - Users are unavailable by default (opt-in model)
 * - "Available - ..." labels mark when a user is available
 * - "Blocked - ..." labels override or restrict availability
 * - "Match Derived" entries are auto-generated from assigned matches
 */
export const useAvailabilityStore = defineStore('availabilityStore', {
	state: (): AvailabilityStoreState => ({
		blocks: [],
		suggestions: [],
		matchDates: [],
		loading: false,
		saving: false,
		error: null,
		selectedDates: [],
		dragStart: null,
		dragEnd: null,
		isDragging: false,
		calendarView: 'month',
		mobileMode: window.innerWidth < 768,
		lastFetchFrom: null,
		lastFetchTo: null,
		resizeHandler: null
	}),
	getters: {
		blocksByDate: (state) => {
			const map = new Map<string, AvailabilityEntry[]>();
			for (const block of state.blocks) {
				const startDate = dayjs(block.startDate);
				const endDate = dayjs(block.endDate);
				let current = startDate;
				while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
					const key = current.format('YYYY-MM-DD');
					const existingBlocks = map.get(key);
					if (existingBlocks) {
						existingBlocks.push(block);
					} else {
						map.set(key, [block]);
					}
					current = current.add(1, 'day');
				}
			}
			return map;
		},
		availabilityEntries: (state) => {
			return state.blocks.filter((block) => isAvailabilityBlockType(block.blockType));
		},
		blockEntries: (state) => {
			return state.blocks.filter((block) => isBlockingBlockType(block.blockType));
		},
		hasAvailabilityForDate: (state) => (dateStr: string) => {
			return state.blocks.some((block) => {
				if (!isAvailabilityBlockType(block.blockType)) {
					return false;
				}
				return coversDate(block, dateStr);
			});
		},
		getScopedAvailabilityStatus:
			(state) =>
			(input: ScopedAvailabilityInput): ScopedAvailabilityStatus => {
				const dateStr = dayjs(input.date).format('YYYY-MM-DD');
				const desiredScope = input.scope ?? BLOCK_TYPES.AVAILABLE_GLOBAL;
				const desiredClient = input.clientNumber ?? null;
				const desiredGroup = input.groupNumber ?? null;
				const dayBlocks = state.blocks.filter((block) => coversDate(block, dateStr));
				// An assigned match is represented on the calendar as Match Derived, but it
				// is not an all-day availability block. Treating it as one prevents an
				// official with all-day availability from taking another compatible match.
				const hasBlockingEntry = dayBlocks.some(
					(block) =>
						block.blockType !== BLOCK_TYPES.MATCH_DERIVED &&
						isBlockingBlockType(block.blockType) &&
						blockingEntryAppliesToMatch(block, desiredGroup, desiredClient) &&
						entryOverlapsMatchWindow(block, dayjs(dateStr).startOf('day'), input.startTime, input.endTime)
				);
				const hasAnyAvailability = dayBlocks.some((block) => isAvailabilityBlockType(block.blockType));
				const hasScopedAvailability = dayBlocks.some((block) => scopesMatchEntry(block, desiredScope, desiredGroup, desiredClient));
				return {
					date: dateStr,
					hasAnyAvailability,
					hasScopedAvailability,
					hasBlockingEntry
				};
			},
		hasBlockForDate: (state) => (dateStr: string) => {
			return state.blocks.some((block) => {
				if (!isBlockingBlockType(block.blockType)) {
					return false;
				}
				return coversDate(block, dateStr);
			});
		},
		matchCountForDate: (state) => (dateStr: string) => {
			const suggestion = state.suggestions.find((item) => dayjs(item.date).format('YYYY-MM-DD') === dateStr);
			return suggestion?.matchCount || 0;
		},
		matchesForDate: (state) => (dateStr: string) => {
			const suggestion = state.suggestions.find((item) => dayjs(item.date).format('YYYY-MM-DD') === dateStr);
			return suggestion?.matches || [];
		},
		datesNeedingAttention: (state) => {
			const today = dayjs().startOf('day');
			const matchDates = state.suggestions.filter((suggestion) => {
				if (!suggestion.matchDay) {
					return false;
				}
				const date = dayjs(suggestion.date).startOf('day');
				return date.isSame(today) || date.isAfter(today);
			});
			return matchDates
				.filter((suggestion) => {
					const dateStr = dayjs(suggestion.date).format('YYYY-MM-DD');
					const hasAvailabilityCoverage = state.blocks.some((block) => {
						if (!isAvailabilityBlockType(block.blockType)) {
							return false;
						}
						return coversDate(block, dateStr);
					});
					const hasBlockCoverage = state.blocks.some((block) => {
						if (!isBlockingBlockType(block.blockType)) {
							return false;
						}
						return coversDate(block, dateStr);
					});
					return !hasAvailabilityCoverage && !hasBlockCoverage;
				})
				.map((suggestion) => suggestion.date);
		},
		attentionCount() {
			return this.datesNeedingAttention.length;
		},
		selectedDateRange: (state) => {
			if (!state.dragStart) {
				return [];
			}
			const start = dayjs(state.dragStart);
			const end = state.dragEnd ? dayjs(state.dragEnd) : start;
			const dates: string[] = [];
			let current = start.isBefore(end) ? start : end;
			const last = start.isBefore(end) ? end : start;
			while (current.isBefore(last) || current.isSame(last, 'day')) {
				dates.push(current.format('YYYY-MM-DD'));
				current = current.add(1, 'day');
			}
			return dates;
		}
	},
	actions: {
		async fetchUserBlocks(userId: number, options: FetchUserBlocksOptions = {}): Promise<ActionResult<AvailabilityEntry[]>> {
			if (!Number.isFinite(userId) || userId <= 0) {
				return {
					success: false,
					error: 'A valid user number is required.'
				};
			}

			const params: Record<string, string> = {};
			if (options.from) params['from'] = dayjs(options.from).format('YYYY-MM-DD');
			if (options.to) params['to'] = dayjs(options.to).format('YYYY-MM-DD');

			try {
				const response = await apiClient.get<AvailabilityEntry[]>(`/availability/blocks/user/${String(userId)}`, {
					params
				});
				return {
					success: true,
					data: normalizeAvailabilityEntries(response.data)
				};
			} catch (error: unknown) {
				const errorMessage = getErrorMessage(error, "Could not load the official's availability blocks.");
				logger.error('[AvailabilityStore] Failed to fetch user blocks', {
					error: errorMessage,
					userId
				});
				return {
					success: false,
					error: errorMessage
				};
			}
		},
		async fetchMyBlocks(from: string | null = null, to: string | null = null) {
			const startDate = from || dayjs().format('YYYY-MM-DD');
			const endDate = to || dayjs().add(60, 'day').format('YYYY-MM-DD');
			logger.info('[AvailabilityStore] Fetching blocks', {
				from: startDate,
				to: endDate
			});
			this.loading = true;
			this.error = null;
			try {
				const response = await apiClient.get<AvailabilityEntry[]>('/availability/blocks/my-blocks', {
					params: {
						from: startDate,
						to: endDate
					}
				});
				this.blocks = normalizeAvailabilityEntries(response.data);
				this.lastFetchFrom = startDate;
				this.lastFetchTo = endDate;
				logger.info('[AvailabilityStore] Fetched blocks', {
					count: this.blocks.length
				});
			} catch (error: unknown) {
				logger.error('[AvailabilityStore] Error fetching blocks', error);
				this.error = getErrorMessage(error, 'Failed to fetch availability');
				this.blocks = [];
			} finally {
				this.loading = false;
			}
		},
		async fetchMySuggestions(from: string | null = null, limit = 30) {
			const startDate = from || dayjs().format('YYYY-MM-DD');
			logger.info('[AvailabilityStore] Fetching suggestions', {
				from: startDate,
				limit
			});
			try {
				const response = await apiClient.get<AvailabilitySuggestion[]>('/availability/suggestions/my-suggestions', {
					params: {
						from: startDate,
						limit
					}
				});
				const incoming = response.data;
				const merged = new Map<string, AvailabilitySuggestion>();
				for (const existing of this.suggestions) {
					const key = dayjs(existing.date).format('YYYY-MM-DD');
					merged.set(key, existing);
				}
				for (const item of incoming) {
					const key = dayjs(item.date).format('YYYY-MM-DD');
					merged.set(key, item);
				}
				this.suggestions = Array.from(merged.values()).sort((left, right) => dayjs(left.date).valueOf() - dayjs(right.date).valueOf());
				logger.info('[AvailabilityStore] Fetched suggestions', {
					incomingCount: incoming.length,
					totalCount: this.suggestions.length
				});
			} catch (error: unknown) {
				logger.error('[AvailabilityStore] Error fetching suggestions', error);
			}
		},
		async checkAvailabilityDetailed(date: string | Date, time: string | null = null, groupId: number | null = null, clientId: number | null = null) {
			const dateStr = dayjs(date).format('YYYY-MM-DD');
			const userId = getStoredUserNumber();
			if (!userId) {
				logger.warn('[AvailabilityStore] No user ID for detailed availability check');
				return null;
			}
			try {
				const params: Record<string, string | number> = {};
				if (time) {
					params['time'] = time;
				}
				if (groupId) {
					params['groupId'] = groupId;
				}
				if (clientId) {
					params['clientId'] = clientId;
				}
				const response = await apiClient.get<AvailabilityCheckResponse>(`/availability/check/user/${String(userId)}/date/${dateStr}`, { params });
				const data = response.data;
				return {
					...data,
					availabilityEntries: normalizeAvailabilityEntries(data.availabilityEntries),
					blockingEntries: normalizeAvailabilityEntries(data.blockingEntries)
				};
			} catch (error: unknown) {
				logger.error('[AvailabilityStore] Error checking detailed availability', error);
				return null;
			}
		},
		/**
		 * Fetch officials who are available for a specific match.
		 *
		 * This request is intentionally lazy and should only be called when the
		 * assignor explicitly requests the availability list.
		 */
		async fetchAvailableOfficialsForMatch(request: AvailableOfficialsRequest): Promise<ActionResult<AvailableOfficialDTO[]>> {
			if (shouldThrottle('fetchAvailableOfficialsForMatch', 250)) {
				posthog.capture('throttled_action', {
					action: 'fetchAvailableOfficialsForMatch'
				});
				return {
					success: false,
					error: 'Too many requests'
				};
			}
			const matchNumber = request.matchNumber;
			const groupId = request.groupId;
			if (!Number.isFinite(matchNumber) || matchNumber <= 0) {
				return {
					success: false,
					error: 'A valid match number is required.'
				};
			}
			if (!Number.isFinite(groupId) || groupId <= 0) {
				return {
					success: false,
					error: 'A valid group number is required.'
				};
			}
			const payload: AvailableOfficialsRequest = {
				groupId,
				clientId: request.clientId ?? null,
				locationId: request.locationId ?? null,
				allowedRoles: request.allowedRoles?.length ? request.allowedRoles : ['OFFICIAL'],
				filterMode: request.filterMode || 'default',
				matchNumber,
				matchDate: request.matchDate ?? null,
				matchTime: request.matchTime ?? null,
				includeAvailability: request.includeAvailability !== false
			};
			logger.info('[AvailabilityStore] Fetching available officials for match', {
				matchNumber: payload.matchNumber,
				groupId: payload.groupId,
				clientId: payload.clientId,
				locationId: payload.locationId
			});
			try {
				const response = await apiClient.post<AvailableOfficialDTO[]>('/autocomplete/officials/available', payload);
				const officials = Array.isArray(response.data) ? response.data : [];
				logger.info('[AvailabilityStore] Available officials fetched', {
					matchNumber: payload.matchNumber,
					groupId: payload.groupId,
					count: officials.length
				});
				posthog.capture('available_officials_loaded', {
					match_number: payload.matchNumber,
					group_number: payload.groupId,
					result_count: officials.length
				});
				return {
					success: true,
					data: officials
				};
			} catch (error: unknown) {
				const errorMessage = getErrorMessage(error, 'Could not load available officials.');
				logger.error('[AvailabilityStore] Failed to fetch available officials', {
					error: errorMessage,
					matchNumber: payload.matchNumber,
					groupId: payload.groupId
				});
				return {
					success: false,
					error: errorMessage
				};
			}
		},
		async quickAddAvailability(dates: Array<string | Date>, options: QuickAddOptions = {}) {
			if (shouldThrottle('quickAddAvailability', 500)) {
				posthog.capture('throttled_action', {
					action: 'quickAddAvailability'
				});
				return {
					success: false,
					error: 'Too many requests'
				};
			}
			const { scope = BLOCK_TYPES.AVAILABLE_GLOBAL, groupNumber = null, clientNumber = null, startTime = null, endTime = null, allDay = true, notes = null } = options;
			const payload = {
				dates: dates.map((date) => dayjs(date).format('YYYY-MM-DD')),
				scope,
				groupNumber,
				clientNumber,
				startTime,
				endTime,
				allDay,
				notes
			};
			logger.info('[AvailabilityStore] Quick adding availability', {
				dates: payload.dates,
				scope
			});
			this.saving = true;
			try {
				const response = await apiClient.post<AvailabilityEntry[]>('/availability/quick-add', payload);
				logger.info('[AvailabilityStore] Quick add success', {
					created: response.data.length
				});
				await this.fetchMyBlocks(this.lastFetchFrom, this.lastFetchTo);
				await this.fetchMySuggestions();
				posthog.capture('availability_quick_add', {
					dates_count: dates.length,
					scope,
					all_day: allDay
				});
				return {
					success: true,
					data: response.data
				};
			} catch (error: unknown) {
				logger.error('[AvailabilityStore] Quick add failed', error);
				return {
					success: false,
					error: getErrorMessage(error)
				};
			} finally {
				this.saving = false;
			}
		},
		async copyMonthAvailability(sourceMonth: string, targetMonth: string): Promise<ActionResult> {
			if (shouldThrottle('copyMonthAvailability', 1000)) {
				posthog.capture('throttled_action', {
					action: 'copyMonthAvailability'
				});
				return {
					success: false,
					error: 'Too many requests'
				};
			}
			logger.info('[AvailabilityStore] Copying availability', {
				from: sourceMonth,
				to: targetMonth
			});
			this.saving = true;
			try {
				const sourceStart = dayjs(`${sourceMonth}-01`).startOf('month');
				const sourceEnd = sourceStart.endOf('month');
				const sourceBlocks = this.blocks.filter((block) => {
					const blockDateValue = (block as AvailabilityEntryWithBlockDate).blockDate ?? block.startDate;
					const blockDate = dayjs(blockDateValue);
					return blockDate.isAfter(sourceStart.subtract(1, 'day')) && blockDate.isBefore(sourceEnd.add(1, 'day')) && isAvailabilityBlockType(block.blockType);
				});
				if (sourceBlocks.length === 0) {
					logger.info('[AvailabilityStore] No availability to copy from source month');
					return {
						success: true,
						copied: 0,
						message: `No availability found in ${sourceMonth}`
					};
				}
				const byDayOfWeek: Record<string, AvailabilityEntry> = {};
				for (const block of sourceBlocks) {
					const blockDate = (block as AvailabilityEntryWithBlockDate).blockDate ?? block.startDate;
					const dayOfWeek = dayjs(blockDate).format('ddd').toUpperCase();
					if (!byDayOfWeek[dayOfWeek]) {
						byDayOfWeek[dayOfWeek] = block;
					}
				}
				const targetStart = dayjs(`${targetMonth}-01`).startOf('month');
				const targetEnd = targetStart.endOf('month');
				const targetDates: Array<{
					date: string;
					template: AvailabilityEntry;
				}> = [];
				let current = targetStart;
				while (current.isBefore(targetEnd) || current.isSame(targetEnd, 'day')) {
					const dayOfWeek = current.format('ddd').toUpperCase();
					const template = byDayOfWeek[dayOfWeek];
					if (template) {
						targetDates.push({
							date: current.format('YYYY-MM-DD'),
							template
						});
					}
					current = current.add(1, 'day');
				}
				if (targetDates.length === 0) {
					return {
						success: true,
						copied: 0,
						message: 'No matching days to copy'
					};
				}
				const scopeGroups: Record<
					string,
					{
						dates: string[];
						template: AvailabilityEntry;
					}
				> = {};
				for (const { date, template } of targetDates) {
					const key = (template.blockType || '') satisfies string;
					if (!scopeGroups[key]) {
						scopeGroups[key] = {
							dates: [],
							template
						};
					}
					scopeGroups[key].dates.push(date);
				}
				let totalCopied = 0;
				for (const group of Object.values(scopeGroups)) {
					const result = await this.quickAddAvailability(group.dates, {
						scope: (group.template.blockType || BLOCK_TYPES.AVAILABLE_GLOBAL) as AvailabilityBlockType,
						groupNumber: group.template.group?.groupNumber || null,
						clientNumber: group.template.client?.clientNumber || null,
						startTime: group.template.startTime ?? null,
						endTime: group.template.endTime ?? null,
						allDay: group.template.allDay ?? true,
						notes: `Copied from ${sourceMonth}`
					});
					if (result.success) {
						totalCopied += group.dates.length;
					}
				}
				posthog.capture('availability_month_copied', {
					source_month: sourceMonth,
					target_month: targetMonth,
					days_copied: totalCopied
				});
				logger.info('[AvailabilityStore] Copy complete', {
					copied: totalCopied
				});
				return {
					success: true,
					copied: totalCopied
				};
			} catch (error: unknown) {
				logger.error('[AvailabilityStore] Copy month failed', error);
				return {
					success: false,
					error: getErrorMessage(error, 'Failed to copy month availability')
				};
			} finally {
				this.saving = false;
			}
		},
		async saveBlock(block: SaveBlockInput): Promise<ActionResult<AvailabilityEntry>> {
			if (shouldThrottle('saveBlock', 500)) {
				posthog.capture('throttled_action', {
					action: 'saveBlock'
				});
				return {
					success: false,
					error: 'Too many requests'
				};
			}
			const payload = createSaveBlockPayload(block);
			logger.info('[AvailabilityStore] Saving block', {
				blockType: payload.blockType
			});
			this.saving = true;
			try {
				const response = await apiClient.post<AvailabilityEntry>('/availability/blocks/save', payload);
				const savedEntry = normalizeAvailabilityEntry(response.data);
				logger.info('[AvailabilityStore] Block saved', {
					id: savedEntry.id
				});
				await this.fetchMyBlocks(this.lastFetchFrom, this.lastFetchTo);
				await this.fetchMySuggestions();
				posthog.capture('availability_block_saved', {
					block_type: payload.blockType,
					is_recurring: payload.recurring,
					is_all_day: payload.allDay
				});
				return {
					success: true,
					data: savedEntry
				};
			} catch (error: unknown) {
				logger.error('[AvailabilityStore] Save block failed', error);
				return {
					success: false,
					error: getErrorMessage(error, 'Failed to save block')
				};
			} finally {
				this.saving = false;
			}
		},
		async saveBlocks(blocks: SaveBlockInput[]): Promise<ActionResult<AvailabilityEntry[]>> {
			if (blocks.length === 0) {
				return { success: true, data: [] };
			}
			if (shouldThrottle('saveBlocks', 500)) {
				posthog.capture('throttled_action', {
					action: 'saveBlocks'
				});
				return {
					success: false,
					error: 'Too many requests'
				};
			}

			const payloads = blocks.map(createSaveBlockPayload);
			logger.info('[AvailabilityStore] Saving blocks', {
				count: payloads.length
			});
			this.saving = true;
			try {
				const responses = await Promise.all(payloads.map((payload) => apiClient.post<AvailabilityEntry>('/availability/blocks/save', payload)));
				const savedEntries = responses.map((response) => normalizeAvailabilityEntry(response.data));
				await this.fetchMyBlocks(this.lastFetchFrom, this.lastFetchTo);
				await this.fetchMySuggestions();
				posthog.capture('availability_blocks_saved', {
					count: savedEntries.length
				});
				return {
					success: true,
					data: savedEntries
				};
			} catch (error: unknown) {
				logger.error('[AvailabilityStore] Save blocks failed', error);
				return {
					success: false,
					error: getErrorMessage(error, 'Failed to save blocks')
				};
			} finally {
				this.saving = false;
			}
		},
		async updateBlock(id: number, updates: SaveBlockInput): Promise<ActionResult<AvailabilityEntry>> {
			if (shouldThrottle('updateBlock', 500)) {
				posthog.capture('throttled_action', {
					action: 'updateBlock'
				});
				return {
					success: false,
					error: 'Too many requests'
				};
			}

			logger.info('[AvailabilityStore] Updating block', { id });
			this.saving = true;
			try {
				const response = await apiClient.patch<AvailabilityEntry>(`/availability/edit/${String(id)}`, updates);
				const updatedEntry = normalizeAvailabilityEntry(response.data);
				logger.info('[AvailabilityStore] Block updated', { id });
				await this.fetchMyBlocks(this.lastFetchFrom, this.lastFetchTo);
				await this.fetchMySuggestions();
				return {
					success: true,
					data: updatedEntry
				};
			} catch (error: unknown) {
				logger.error('[AvailabilityStore] Update block failed', error);
				return {
					success: false,
					error: getErrorMessage(error, 'Failed to update block')
				};
			} finally {
				this.saving = false;
			}
		},
		async deleteBlock(id: number): Promise<ActionResult> {
			const result = await this.deleteBlocks([id]);
			return result.success ? { success: true } : { success: false, error: result.error || 'Failed to delete block' };
		},
		async deleteBlocks(ids: number[]): Promise<ActionResult<number[]>> {
			const uniqueIds = [...new Set(ids)];
			if (uniqueIds.length === 0) return { success: true, data: [] };
			if (uniqueIds.some((id) => !Number.isInteger(id) || id <= 0)) {
				return { success: false, error: 'A valid availability ID is required for each block.' };
			}
			if (this.saving) {
				return { success: false, error: 'Another availability change is still saving. Please try again.' };
			}
			// Throttle the whole action so multiple entries can be deleted together.
			if (shouldThrottle('deleteBlock', 500)) {
				posthog.capture('throttled_action', {
					action: 'deleteBlock'
				});
				return {
					success: false,
					error: 'Too many requests'
				};
			}
			logger.info('[AvailabilityStore] Deleting blocks', { ids: uniqueIds });
			this.saving = true;
			try {
				const results = await Promise.allSettled(
					uniqueIds.map(async (id) => {
						await apiClient.delete(`/availability/${String(id)}`);
						this.blocks = this.blocks.filter((block) => block.id !== id);
						posthog.capture('availability_block_deleted');
						return id;
					})
				);
				const deletedIds: number[] = [];
				const errors: string[] = [];
				for (const result of results) {
					if (result.status === 'fulfilled') {
						deletedIds.push(result.value);
					} else {
						logger.error('[AvailabilityStore] Delete block failed', result.reason);
						errors.push(getErrorMessage(result.reason, 'Failed to delete block'));
					}
				}
				if (deletedIds.length > 0) await this.fetchMySuggestions();
				if (errors.length > 0) {
					return {
						success: false,
						data: deletedIds,
						error: deletedIds.length > 0 ? `Removed ${deletedIds.length} of ${uniqueIds.length} blocks. Please try again to clear the remaining blocks.` : errors.join(' ')
					};
				}
				return { success: true, data: deletedIds };
			} finally {
				this.saving = false;
			}
		},
		updateMobileMode() {
			this.mobileMode = window.innerWidth < 768;
		},
		initialize() {
			logger.info('[AvailabilityStore] Initializing');
			if (!this.resizeHandler) {
				this.resizeHandler = () => {
					this.updateMobileMode();
				};
				window.addEventListener('resize', this.resizeHandler);
			}
			this.updateMobileMode();
		}
	}
});
